import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import ErrorBoundary from './components/ErrorBoundary';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

// Components
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import DataVisualization from './components/DataVisualization';
import UserManual from './components/UserManual';
import Community from './components/Community';
import CallToAction from './components/CallToAction';
import Footer from './components/Footer';


// Import content pages (these will be created later) with theme context persistence
const HowItWorks = React.lazy(() => import('./pages/HowItWorks').then(module => ({ default: withTheme(module.default) })));
const ContributionGuidelines = React.lazy(() => import('./pages/ContributionGuidelines').then(module => ({ default: withTheme(module.default) })));
const CaseStudies = React.lazy(() => import('./pages/CaseStudies').then(module => ({ default: withTheme(module.default) })));
const FAQ = React.lazy(() => import('./pages/FAQ').then(module => ({ default: withTheme(module.default) })));
const ApiDocs = React.lazy(() => import('./pages/ApiDocs').then(module => ({ default: withTheme(module.default) })));
const Crowdsourcing = React.lazy(() => import('./pages/Crowdsourcing').then(module => ({ default: withTheme(module.default) })));

// Define theme
const theme = {
  colors: {
    primary: '#4a9cff',
    primaryLight: '#e0f2ff',
    primaryDark: '#0078ff',
    secondary: '#ff8e56',
    accent: '#ffdb58',
    background: '#ffffff',
    backgroundAlt: '#f7fafc',
    backgroundDark: '#1a2a3a',
    text: '#333333',
    textLight: '#a0aec0',
    border: '#e2e8f0',
    white: '#ffffff'
  },
  shadows: {
    small: '0 2px 8px rgba(0, 0, 0, 0.05)',
    medium: '0 4px 16px rgba(0, 0, 0, 0.1)',
    large: '0 8px 30px rgba(0, 0, 0, 0.15)'
  },
  breakpoints: {
    mobile: '576px',
    tablet: '768px',
    laptop: '992px',
    desktop: '1200px'
  },
  fonts: {
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
  }
};

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  html {
    scroll-behavior: smooth;
  }
  
  body {
    font-family: ${props => props.theme.fonts.body};
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.heading};
    margin: 0 0 1rem;
  }
  
  a {
    text-decoration: none;
    color: ${props => props.theme.colors.primary};
    transition: color 0.3s ease;
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  p {
    margin: 0 0 1rem;
  }
  
  section {
    overflow: hidden;
  }
`;

// Loading animation
const LoadingOverlay = styled.div.attrs(props => ({
  style: {
    opacity: props.isLoading ? 1 : 0,
    visibility: props.isLoading ? 'visible' : 'hidden'
  }
}))`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.colors.background};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s ease, visibility 0.5s ease;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(74, 156, 255, 0.2);
  border-radius: 50%;
  border-top-color: ${props => props.theme.colors.primary};
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Error fallback when lazy-loaded components fail
const ErrorFallback = styled.div`
  padding: 100px 20px;
  text-align: center;
  color: ${props => props.theme.colors.text};
`;

// Helper HOC to ensure theme context is passed to lazy-loaded components
const withTheme = (Component) => (props) => (
  <ThemeProvider theme={theme}>
    <GlobalStyle />
    <Component {...props} />
  </ThemeProvider>
);

// Main App component
function App({ defaultLanguage, page }) {
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set language based on prop or URL
  useEffect(() => {
    if (defaultLanguage) {
      // This will be set by the LanguageProvider
      document.documentElement.lang = defaultLanguage;
    }
  }, [defaultLanguage]);
  
  // Track page views and perform SEO operations
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-WEATHERTELLER', {
        page_path: location.pathname + location.search
      });
    }
    
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);
  
  useEffect(() => {
    // Simulate loading of resources
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Function to render the appropriate page content
  const renderContent = () => {
    // If a specific page is requested
    if (page) {
      switch (page) {
        case 'how-it-works':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading How It Works...</ErrorFallback>}>
              <HowItWorks />
            </React.Suspense>
          );
        
        case 'crowdsourcing':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading Crowdsourcing...</ErrorFallback>}>
              <Crowdsourcing />
            </React.Suspense>
          );
        
        case 'contribution-guidelines':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading Contribution Guidelines...</ErrorFallback>}>
              <ContributionGuidelines />
            </React.Suspense>
          );
        case 'case-studies':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading Case Studies...</ErrorFallback>}>
              <CaseStudies />
            </React.Suspense>
          );
        case 'faq':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading FAQ...</ErrorFallback>}>
              <FAQ />
            </React.Suspense>
          );
        case 'api-docs':
          return (
            <React.Suspense fallback={<ErrorFallback>Loading API Documentation...</ErrorFallback>}>
              <ApiDocs />
            </React.Suspense>
          );
        default:
          return null;
      }
    }
    
    // Default homepage content
    return (
      <>
        <ErrorBoundary>
          <Hero />
          <Features />
        </ErrorBoundary>
        
        <ErrorBoundary>
          <DataVisualization />
        </ErrorBoundary>
        
        <ErrorBoundary>
          <UserManual />
          <Community />
          <CallToAction />
        </ErrorBoundary>
      </>
    );
  };
  
  // SEO metadata based on page
  const getSeoData = () => {
    // Default SEO data
    let seoData = {
      title: 'WeatherTeller - Crowdsourced Weather Reporting Platform',
      description: 'Crowdsourced weather reporting platform using geolocation data for hyperlocal, real-time weather intelligence',
      canonicalUrl: 'https://www.weatherteller.com'
    };
    
    // Adjust SEO data based on page
    if (page) {
          switch (page) {
            case 'how-it-works':
              seoData = {
                ...seoData,
                title: 'How WeatherTeller Works - Crowdsourced Weather Methodology',
                description: 'Learn how WeatherTeller combines crowdsourced data with geolocation to deliver precise weather reporting',
                canonicalUrl: 'https://www.weatherteller.com/how-it-works'
              };
              break;
            case 'crowdsourcing':
              seoData = {
                ...seoData,
                title: 'Weather Crowdsourcing | The Power of Community in Modern Meteorology',
                description: 'Discover how weather crowdsourcing transforms data collection through citizen scientists, enabling more accurate and hyperlocal weather intelligence',
                canonicalUrl: 'https://www.weatherteller.com/crowdsourcing'
              };
              break;
            case 'contribution-guidelines':
          seoData = {
            ...seoData,
            title: 'Contribution Guidelines - WeatherTeller Community',
            description: 'Learn how to contribute to the WeatherTeller platform and become part of our weather reporting community',
            canonicalUrl: 'https://www.weatherteller.com/contribute'
          };
          break;
        case 'case-studies':
          seoData = {
            ...seoData,
            title: 'WeatherTeller Case Studies - Real-World Applications',
            description: 'Explore how businesses and communities use WeatherTeller for accurate hyperlocal weather intelligence',
            canonicalUrl: 'https://www.weatherteller.com/case-studies'
          };
          break;
        case 'faq':
          seoData = {
            ...seoData,
            title: 'WeatherTeller FAQ - Common Questions Answered',
            description: 'Find answers to frequently asked questions about WeatherTeller\'s crowdsourced weather platform',
            canonicalUrl: 'https://www.weatherteller.com/faq'
          };
          break;
        case 'api-docs':
          seoData = {
            ...seoData,
            title: 'WeatherTeller API Documentation',
            description: 'Technical documentation for integrating with WeatherTeller\'s hyperlocal weather data API',
            canonicalUrl: 'https://www.weatherteller.com/api-docs'
          };
          break;
        default:
          break;
      }
    }
    
    return seoData;
  };
  
  const seoData = getSeoData();
  
  return (
    <HelmetProvider>
      <LanguageProvider initialLanguage={defaultLanguage}>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <Helmet>
            <title>{seoData.title}</title>
            <meta name="description" content={seoData.description} />
            <link rel="canonical" href={seoData.canonicalUrl} />
          </Helmet>
        
        <LoadingOverlay isLoading={isLoading}>
          <Spinner />
        </LoadingOverlay>
        
          <ErrorBoundary>
            <Header />
          </ErrorBoundary>
          
          {renderContent()}
          
          <ErrorBoundary>
            <Footer />
          </ErrorBoundary>
        </ThemeProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;